import { Injectable } from "@angular/core";

import { Observable, Subscriber } from 'rxjs';
import { take } from 'rxjs/operators';

import { DialogService, SpinnerService } from 'dspx-core';

import { API_ENDPOINTS } from '../constants/api-endpoints.constants';

import { ExcelSheetColumnDataType } from "../interfaces/expected-sheet-configuration.interface";
import { ExcelSheetColumnValidationErrorType, ParsedExcelSheet } from "../interfaces/parsed-excel-sheet.interface";

import { ReferenceDataCategory } from '../models/reference-data-category';

import { AppDataService } from '../services/app-data.service';
import { ErrorService } from '../services/error.service';
import { ExcelParserService } from "./excel-parser.service";

@Injectable({
  providedIn: 'root'
})
export class ExcelImportService {

  constructor(
    private errorService: ErrorService,
    private excelParserService: ExcelParserService,
    private spinnerService: SpinnerService,
    private dialogService: DialogService,
    private appDataService: AppDataService) {
  }

  uploadReferenceData(file: File): Observable<void> {
    return this.uploadSheet(file, 'refDataUploadFileType', (subscriber, event) => {

      const sheet = this.excelParserService.parseSheet(event.target.result, {
        columns: [
          { headerName: 'CategoryCode', isMandatory: true, isMandatoryErrorCode: 'refDataCategoryCodeNotFound' },
          { headerName: 'CategoryText', isMandatory: true, isMandatoryErrorCode: 'refDataCategoryTextNotFound' },
          { headerName: 'IsCategoryActive', isMandatory: true, isMandatoryErrorCode: 'refDataUploadIsCategoryActiveInvalid', dataType: ExcelSheetColumnDataType.Boolean, dataTypeErrorCode: 'refDataUploadIsCategoryActiveInvalid' },
          { headerName: 'ParentCategoryCode' },
          { headerName: 'ValueCode', isMandatory: true, isMandatoryErrorCode: 'refDataValueCodeNotFound' },
          { headerName: 'ValueText', isMandatory: true, isMandatoryErrorCode: 'refDataValueTextNotFound' },
          { headerName: 'IsValueActive', isMandatory: true, isMandatoryErrorCode: 'refDataUploadIsValueActiveInvalid', dataType: ExcelSheetColumnDataType.Boolean, dataTypeErrorCode: 'refDataUploadIsValueActiveInvalid' },
          { headerName: 'DisplayOrder', isMandatory: true, isMandatoryErrorCode: 'refDataUploadDisplayOrderNotFound', dataType: ExcelSheetColumnDataType.Integer, dataTypeErrorCode: 'refDataUploadDisplayOrderInvalid' },
          { headerName: 'ParentValueCode' }
        ]
      });
      if (!this.isSheetValid(subscriber, sheet, 'refDataUploadCsvExpectedColumnHeaders', 'refDataUploadCsvExpectedColumns', 'refDataUploadNoData')) {
        return;
      }

      const entities: ReferenceDataCategory[] = [];
      for (let i = 2; i <= sheet.actualLastRowNumber; i++) {

        // Read the row data
        const categoryCode = sheet.getCellValue('CategoryCode', i);
        const categoryText = sheet.getCellValue('CategoryText', i);
        const isCategoryActive = sheet.getCellValue('IsCategoryActive', i);
        const parentCategoryCode = sheet.getCellValue('ParentCategoryCode', i);
        const valueCode = sheet.getCellValue('ValueCode', i);
        const valueText = sheet.getCellValue('ValueText', i);
        const isValueActive = sheet.getCellValue('IsValueActive', i);
        const displayOrder = sheet.getCellValue('DisplayOrder', i);
        const parentValueCode = sheet.getCellValue('ParentValueCode', i);

        // Add the row data to the array of entities
        const existingEntity = entities.find(e => e.code === categoryCode);
        const entity = existingEntity ?? {
          code: categoryCode,
          text: categoryText,
          isActive: this.excelParserService.toBoolean(isCategoryActive),
          parentCategoryCode: parentCategoryCode,
          values: []
        };
        entity.values.push({
          code: valueCode,
          text: valueText,
          isActive: this.excelParserService.toBoolean(isValueActive),
          displayOrder: this.excelParserService.toInteger(displayOrder),
          parentValueCode: parentValueCode
        });
        if (!existingEntity) {
          entities.push(entity);
        }
      }

      // Duplicate checks
      if (this.sheetHasDuplicates(subscriber, 'refDataUploadDuplicateValue', entities.some(e => this.excelParserService.hasDuplicateProperties(e.values, i => [i.code])))) {
        return;
      }
      if (this.sheetHasDuplicates(subscriber, 'refDataUploadDuplicateDisplayOrder', entities.some(e => this.excelParserService.hasDuplicateProperties(e.values, i => [i.displayOrder.toString()])))) {
        return;
      }

      this.postSheetData(subscriber, API_ENDPOINTS.referenceData.bulkSave, entities, 'refDataUploadSuccessful', [
        'refDataUploadNoData',
        'refDataCategoryCodeNotFound',
        'refDataCategoryCodeMaxLength',
        'refDataCategoryCodeAlphaNumeric',
        'refDataCategoryTextNotFound',
        'refDataCategoryTextMaxLength',
        'refDataCategoryTextDuplicatesInsideCsv',
        'refDataCategoryTextDuplicatesWithDatabase',
        'refDataValueCodeNotFound',
        'refDataValueCodeMaxLength',
        'refDataValueCodeAlphaNumeric',
        'refDataUploadDuplicateValue',
        'refDataUploadDuplicateDisplayOrder',
        'refDataValueTextNotFound',
        'refDataValueTextMaxLength',
        'refDataValueTextDuplicatesInsideCsv',
        'refDataValueTextDuplicatesWithDatabase',
        'refDataUploadDisplayOrderNotFound',
        'refDataUploadParentCategoryNotFound',
        'refDataUploadParentValueNotFound'
      ]);
    });
  }

  private uploadSheet(file: File, fileTypeErrorPopupCode: string, processFunction: (subscriber: Subscriber<void>, event: ProgressEvent<FileReader>) => void): Observable<void> {
    return new Observable(subscriber => {
      try {
        const reader = new FileReader();
        reader.onload = event => {
          try {
            processFunction(subscriber, event);
          }
          catch (error) {
            subscriber.error(error);
          }
        };
        if (file) {
          const validFileTypes = [
            'application/vnd.ms-excel',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
            'text/csv'
          ];
          if (validFileTypes.includes(file.type)) {
            reader.readAsBinaryString(file);
          }
          else {
            this.dialogService.showDialog(fileTypeErrorPopupCode);
            subscriber.error();
          }
        }
      }
      catch (error) {
        subscriber.error(error);
      }
    });
  }

  private postSheetData<TEntity>(subscriber: Subscriber<void>, endpoint: string, entities: TEntity[], successPopupCode: string, serverErrorPopupCodes: string[]): void {
    this.appDataService.post(endpoint, entities)
      .pipe(take(1), this.errorService.errorHandler())
      .subscribe(
        data => {
          if (data?.status === 'Success') {
            this.spinnerService.hideSpinner();
            if (serverErrorPopupCodes.includes(data.message)) {
              this.dialogService.showDialog(data.message);
              subscriber.error();
            }
            else {
              this.dialogService.showDialog(successPopupCode);
              subscriber.next();
              subscriber.complete();
            }
          }
        },
        error => {
          subscriber.error(error);
        });
  }

  private isSheetValid(
    subscriber: Subscriber<void>,
    sheet: ParsedExcelSheet,
    hasExpectedHeaderErrorPopupCode: string,
    hasOnlyExpectedHeaderErrorPopupCode: string,
    hasDataErrorPopupCode: string): boolean {

    if (!sheet.hasExpectedHeader) {
      this.dialogService.showDialog(hasExpectedHeaderErrorPopupCode);
      subscriber.error();
      return false;
    }

    if (!sheet.hasOnlyExpectedHeader) {
      this.dialogService.showDialog(hasOnlyExpectedHeaderErrorPopupCode);
      subscriber.error();
      return false;
    }

    if (!sheet.hasData) {
      this.dialogService.showDialog(hasDataErrorPopupCode);
      subscriber.error();
      return false;
    }

    if (sheet.firstColumnValidationError) {
      if (sheet.firstColumnValidationError.errorCode) {
        this.dialogService.showDialog(sheet.firstColumnValidationError.errorCode);
      }
      else {
        const errorMessage = {
          [ExcelSheetColumnValidationErrorType.Mandatory]: `The upload file contains at least one row with an empty value in the ${sheet.firstColumnValidationError.headerName} column.`,
          [ExcelSheetColumnValidationErrorType.DataType ]: `The upload file contains at least one row with an invalid value in the ${sheet.firstColumnValidationError.headerName} column.`
        }[sheet.firstColumnValidationError.errorType];
        this.dialogService.showSimplePopup(`${errorMessage} Please rectify error and try again.`, 'File Upload Validation Error', 'OK');
      }
      subscriber.error();
      return false;
    }

    return true;
  }

  private sheetHasDuplicates(subscriber: Subscriber<void>, duplicateErrorPopupCode: string, hasDuplicates: boolean): boolean {
    if (hasDuplicates) {
      this.dialogService.showDialog(duplicateErrorPopupCode);
      subscriber.error();
      return true;
    }
    return false;
  }

}
